import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { learnerStatusApi } from '@/api/learnerStatus'
import { lcStudentApi } from '@/api/lcStudents'
import { lcTimeslotApi, type Timeslot } from '@/api/lc'
import { tenantApi } from '@/api/tenant'
import { getApiErrorMessage } from '@/utils/apiError'
import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Button } from '@quebon/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@quebon/ui/components/select'
import { Spinner } from '@quebon/ui/components/spinner'
import { Textarea } from '@quebon/ui/components/textarea'

export interface ReEnrollmentLearner {
  userId: number
  fullName: string
}

interface Props {
  learner?: ReEnrollmentLearner
  initialTimeslot?: Timeslot
  onClose: () => void
}

const localToday = (): string => {
  const today = new Date()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${today.getFullYear()}-${month}-${day}`
}

export function ReEnrollmentModal({ learner, initialTimeslot, onClose }: Props) {
  const { t } = useTranslation(['admin', 'common'])
  const queryClient = useQueryClient()
  const initializedUserId = useRef<number | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(learner?.userId)
  const [licenseId, setLicenseId] = useState<number | undefined>()
  const [timeslotId, setTimeslotId] = useState<number | undefined>(initialTimeslot?.timeslotId)
  const [startDate, setStartDate] = useState(localToday)
  const [endDate, setEndDate] = useState('')
  const [reason, setReason] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)

  const withdrawnQ = useQuery({
    queryKey: ['lc', 'students', 're-enroll-candidates'],
    queryFn: () => lcStudentApi.search({ status: 'WITHDRAWN', sort: 'createdAt', dir: 'desc', page: 0, size: 100 }),
    enabled: learner == null,
  })
  const timeslotsQ = useQuery({
    queryKey: ['lcTimeslots'],
    queryFn: () => lcTimeslotApi.getAll(),
  })
  const licensesQ = useQuery({
    queryKey: ['activeLicenses'],
    queryFn: async () => (await tenantApi.getAllActiveLicenses()).data.data ?? [],
  })
  const contextQ = useQuery({
    queryKey: ['lc', 'learnerReEnrollContext', selectedUserId],
    queryFn: () => learnerStatusApi.getReEnrollContext(selectedUserId!),
    enabled: selectedUserId != null,
  })

  const candidates = withdrawnQ.data?.data.data?.content ?? []
  const timeslots = timeslotsQ.data?.data.data ?? []
  const licenses = licensesQ.data ?? []
  const previousEnrollments = contextQ.data?.data.data?.previousEnrollments ?? []
  const learnerItems = learner
    ? [{ value: String(learner.userId), label: learner.fullName }]
    : candidates.map((candidate) => ({
        value: String(candidate.member.userId),
        label: candidate.member.fullName,
      }))
  const licenseItems = licenses
    .filter((license) => previousEnrollments.some((previous) => previous.licenseId === license.id))
    .map((license) => ({
      value: String(license.id),
      label: license.productName ?? license.curriculumTitle ?? `#${license.productId}`,
    }))
  const timeslotItems = timeslots.map((timeslot) => ({
    value: String(timeslot.timeslotId),
    label: timeslot.name,
  }))

  useEffect(() => {
    if (selectedUserId == null || initializedUserId.current === selectedUserId || previousEnrollments.length === 0) return
    const activeLicenseIds = new Set(licenses.map((license) => license.id))
    const activeTimeslotIds = new Set(timeslots.map((timeslot) => timeslot.timeslotId))
    const suggested = previousEnrollments.find((previous) =>
      activeLicenseIds.has(previous.licenseId)
      && (initialTimeslot != null || (previous.previousTimeslotId != null && activeTimeslotIds.has(previous.previousTimeslotId)))
    )
    if (!suggested) return
    setLicenseId(suggested.licenseId)
    if (initialTimeslot == null && suggested.previousTimeslotId != null) {
      setTimeslotId(suggested.previousTimeslotId)
    }
    initializedUserId.current = selectedUserId
  }, [initialTimeslot, licenses, previousEnrollments, selectedUserId, timeslots])

  const selectedPrevious = previousEnrollments.find((previous) => previous.licenseId === licenseId)
    ?? previousEnrollments[0]

  const reEnrollMutation = useMutation({
    mutationFn: () => learnerStatusApi.reEnroll(selectedUserId!, {
      licenseId: licenseId!,
      timeslotId: timeslotId!,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      reason: reason.trim(),
    }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['lc', 'students'] }),
        queryClient.invalidateQueries({ queryKey: ['lcEnrollments'] }),
        queryClient.invalidateQueries({ queryKey: ['lcTimeslots'] }),
        queryClient.invalidateQueries({ queryKey: ['lcAssignable'] }),
        queryClient.invalidateQueries({ queryKey: ['learnerSummary', selectedUserId] }),
      ])
      toast.success(t('admin:students.reEnroll.toast.success'))
      onClose()
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t)),
  })

  const handleLearnerChange = (value: string | null) => {
    const nextUserId = value ? Number(value) : undefined
    setSelectedUserId(nextUserId)
    setLicenseId(undefined)
    if (initialTimeslot == null) setTimeslotId(undefined)
    initializedUserId.current = null
    setValidationError(null)
  }

  const handleSubmit = () => {
    if (selectedUserId == null || licenseId == null || timeslotId == null) {
      setValidationError(t('admin:students.reEnroll.validation.required'))
      return
    }
    if (!reason.trim()) {
      setValidationError(t('admin:students.reEnroll.validation.reasonRequired'))
      return
    }
    if (startDate && endDate && endDate < startDate) {
      setValidationError(t('admin:students.reEnroll.validation.period'))
      return
    }
    setValidationError(null)
    reEnrollMutation.mutate()
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('admin:students.reEnroll.title')}</DialogTitle>
          <DialogDescription>{t('admin:students.reEnroll.description')}</DialogDescription>
        </DialogHeader>

        <FieldGroup>
          <Field data-disabled={learner != null}>
            <FieldLabel>{t('admin:students.reEnroll.fields.learner')}</FieldLabel>
            <Select
              items={learnerItems}
              value={selectedUserId != null ? String(selectedUserId) : null}
              onValueChange={handleLearnerChange}
              disabled={learner != null}
            >
              <SelectTrigger className="w-full" aria-label={t('admin:students.reEnroll.fields.learner')}>
                <SelectValue placeholder={t('admin:students.reEnroll.placeholders.learner')} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {learnerItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </Field>

          {selectedPrevious ? (
            <Alert>
              <AlertTitle>{t('admin:students.reEnroll.previous.title')}</AlertTitle>
              <AlertDescription>
                {t('admin:students.reEnroll.previous.description', {
                  className: selectedPrevious.previousTimeslotName ?? t('admin:students.reEnroll.previous.unknownClass'),
                  date: selectedPrevious.withdrawnAt?.slice(0, 10) ?? '-',
                  reason: selectedPrevious.withdrawalReason ?? t('admin:students.reEnroll.previous.noReason'),
                })}
              </AlertDescription>
            </Alert>
          ) : null}

          <Field data-invalid={validationError != null && licenseId == null}>
            <FieldLabel>{t('admin:students.reEnroll.fields.course')}</FieldLabel>
            <Select
              items={licenseItems}
              value={licenseId != null ? String(licenseId) : null}
              onValueChange={(value) => {
                setLicenseId(value ? Number(value) : undefined)
                setValidationError(null)
              }}
              disabled={selectedUserId == null || contextQ.isLoading}
            >
              <SelectTrigger className="w-full" aria-invalid={validationError != null && licenseId == null}>
                <SelectValue placeholder={t('admin:students.reEnroll.placeholders.course')} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {licenseItems.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <FieldDescription>{t('admin:students.reEnroll.hints.course')}</FieldDescription>
          </Field>

          <Field data-invalid={validationError != null && timeslotId == null}>
            <FieldLabel>{t('admin:students.reEnroll.fields.class')}</FieldLabel>
            <Select
              items={timeslotItems}
              value={timeslotId != null ? String(timeslotId) : null}
              onValueChange={(value) => {
                setTimeslotId(value ? Number(value) : undefined)
                setValidationError(null)
              }}
              disabled={initialTimeslot != null}
            >
              <SelectTrigger className="w-full" aria-invalid={validationError != null && timeslotId == null}>
                <SelectValue placeholder={t('admin:students.reEnroll.placeholders.class')} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {timeslotItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="re-enroll-start-date">{t('admin:students.reEnroll.fields.startDate')}</FieldLabel>
              <Input id="re-enroll-start-date" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
            </Field>
            <Field data-invalid={Boolean(startDate && endDate && endDate < startDate)}>
              <FieldLabel htmlFor="re-enroll-end-date">{t('admin:students.reEnroll.fields.endDate')}</FieldLabel>
              <Input
                id="re-enroll-end-date"
                type="date"
                value={endDate}
                min={startDate || undefined}
                aria-invalid={Boolean(startDate && endDate && endDate < startDate)}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </Field>
          </div>

          <Field data-invalid={validationError != null && !reason.trim()}>
            <FieldLabel htmlFor="re-enroll-reason">{t('admin:students.reEnroll.fields.reason')}</FieldLabel>
            <Textarea
              id="re-enroll-reason"
              value={reason}
              maxLength={1000}
              aria-invalid={validationError != null && !reason.trim()}
              placeholder={t('admin:students.reEnroll.placeholders.reason')}
              onChange={(event) => {
                setReason(event.target.value)
                setValidationError(null)
              }}
            />
            <FieldDescription>{t('admin:students.reEnroll.hints.reason')}</FieldDescription>
            <FieldError>{validationError}</FieldError>
          </Field>
        </FieldGroup>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common:button.cancel')}
          </Button>
          <Button type="button" disabled={reEnrollMutation.isPending} onClick={handleSubmit}>
            {reEnrollMutation.isPending ? <Spinner data-icon="inline-start" /> : null}
            {t('admin:students.reEnroll.actions.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
